import { ArrowsClockwise, Package, ShoppingCart } from "@phosphor-icons/react"

import HoverCardRow from "~/components/reusable/hovercard/HoverCardRow"
import HoverCardRowWrapper from "~/components/reusable/hovercard/HoverCardRowWrapper"
import {
  MetricBreakdownFormula,
  MetricBreakdownHoverCard,
  MetricBreakdownNullState,
} from "~/components/reusable/hovercard/MetricBreakdownHoverCard"
import {
  getInventoryTurnPeriod,
  partialPeriodNote,
} from "~/components/reusable/inventoryTurnPeriod"
import { suffixedCurrencyFormatters } from "~/utils/formatting"
import { roundNumber } from "~/utils/helpers"
import { formatName } from "~/utils/namingUtils"

type Props = {
  cogs: number
  avgInvValue: number
  /** Aggregated cost price for the row. <= 0 / null means no cost price is set on the underlying SKUs. */
  costPrice?: number | null
  currency?: string
  startDate?: Date | string | null
  children: React.ReactNode
}

export function InventoryTurnBreakdownHoverCard({
  cogs,
  avgInvValue,
  costPrice,
  currency,
  startDate,
  children,
}: Props) {
  const turn = avgInvValue > 0 ? cogs / avgInvValue : null
  const missing: string[] = []
  if (costPrice == null || costPrice <= 0) missing.push("cost price")
  if (cogs <= 0) missing.push(formatName("sale", true))
  if (avgInvValue <= 0) missing.push("inventory history with stock")

  const period = getInventoryTurnPeriod(startDate)
  const { compact, full } = suffixedCurrencyFormatters(currency)

  return (
    <MetricBreakdownHoverCard
      title="Inventory turn"
      trigger={children}
      description={partialPeriodNote(period)}
    >
      {turn == null ? (
        <MetricBreakdownNullState
          label="Inventory turn"
          missing={missing}
          recorded={
            cogs > 0 ? { label: formatName("sale", true, true), value: compact(cogs) } : undefined
          }
        />
      ) : (
        <>
          <HoverCardRowWrapper>
            <HoverCardRow
              icon={<ShoppingCart size={14} />}
              label={`COGS (${period.periodLabel})`}
              value={compact(cogs)}
            />
            <HoverCardRow
              icon={<Package size={14} />}
              label={`Avg. monthly inventory (${period.periodLabel})`}
              value={compact(avgInvValue)}
            />
          </HoverCardRowWrapper>
          <HoverCardRowWrapper>
            <HoverCardRow
              icon={<ArrowsClockwise size={14} />}
              label="Inventory turn"
              value={roundNumber(turn, 2).toString()}
            />
          </HoverCardRowWrapper>
          <MetricBreakdownFormula>
            {`${full(cogs)} / ${full(avgInvValue)} = ${roundNumber(turn, 2)}`}
          </MetricBreakdownFormula>
        </>
      )}
    </MetricBreakdownHoverCard>
  )
}
